<script lang="ts">
    import Textfield from '@smui/textfield';
    import TFIcon from '@smui/textfield/icon';
    import Button, {Label} from '@smui/button';
    import Checkbox from '@smui/checkbox';
    import FormField from '@smui/form-field';
    import {Icon} from '@smui/common';

    import CircularProgress from '@smui/circular-progress';
    import {Artist, artists, roseService} from "../rose";
    import Dialog, {Actions, Content, Title} from '@smui/dialog';
    import Autocomplete from "@smui-extra/autocomplete";
    import {Text} from "@smui/list";
    import {navigate} from "svelte-routing";
    import {randomElement, searchCaseAccentInsensitive} from "../utils";
    import CharacterCounter from "@smui/textfield/character-counter";

    const disabled = true;

    let artist: Artist | undefined = undefined;
    let description = null;
    let email = null;
    let checked = false;

    let loading = false;
    let openDialog = false;
    let message = null;

    $: complete = checked && artist && description && email;

    const descriptionExamples = ['Cubist style and psychedelic', 'Minimalist with sky background',
        'Abstract with vivid colors', 'Mediterranean vibes', 'Futuristic dystopia surrealism', 'Splash of colors',
        'Pixel art style'];
    const descriptionExample = randomElement(descriptionExamples);

    async function createRose() {
        if (disabled) {
            return;
        }
        if (!checked || !artist || !description || !email) {
            return;
        }
        loading = true;
        const result = await roseService.createRose(artist.id, description, email);
        if (result.error) {
            message = result.message
            openDialog = true;
        } else {
            const roseId = result.message.split('/')[1].split('.')[0];
            navigate(`/details/${roseId}`);
        }
        loading = false;
    }
</script>

{#if loading}
    <div style="display: flex; justify-content: center">
        <CircularProgress style="height: 32px; width: 32px;" indeterminate/>
    </div>
{:else}
    <div id="form-div">
        <span>
            Aquesta opció ja no està disponible, però encara pots veure totes les roses ja creades.
        </span>
        <p id="creation-recommendation-texts" class:text-disabled={disabled}>
            Selecciona un estil i introdueix una descripció per a la teva rosa. La Intel·ligència Artificial (IA)
            generarà una imatge a partir de la teva descripció i de l'estil de l'artista escollit.
        </p>
        <div id="artist-form-field">
            {#key $artists.length}
                <Autocomplete
                        disabled="{disabled}"
                        name="artist"
                        textfield$required
                        textfield$variant="outlined"
                        style="width: 100%;"
                        textfield$style="width: 100%;"
                        options={$artists}
                        getOptionLabel={(art) => art ? art.name : ''}
                        search="{(input) => searchCaseAccentInsensitive(input, $artists, (art) => art ? art.name : '')}"
                        bind:value={artist}
                        label="Artista">
                    <div slot="no-matches">
                        <Text>Artista no trobat</Text>
                    </div>
                </Autocomplete>
            {/key}
        </div>

        <div id="description-form-field">
            <Textfield style="width: 100%;" name="description" variant="outlined"
                       required bind:value={description} label="Descripció" input$maxlength={60}
                       input$placeholder="Exemple: &quot;{descriptionExample}&quot;"
                       disabled="{disabled}">
                <CharacterCounter slot="helper">0 / 60</CharacterCounter>
            </Textfield>
        </div>
        <p id="creation-disclaimer-text" class:text-disabled={disabled}>
            <Icon class="material-icons">info</Icon>
            La descripció no pot contenir paraules ofensives, humiliants, sexistes, discriminatòries, xenòfobes,
            racistes i/o que atemptin contra la dignitat de la persona.
        </p>

        <div id="email-form-field">
            <Textfield style="width: 100%;" name="email" variant="outlined" type="email"
                       required bind:value={email} label="Correu electrònic"
                       disabled="{disabled}">
                <TFIcon class="material-icons" slot="leadingIcon">email</TFIcon>
            </Textfield>
        </div>
        <FormField>
            <Checkbox bind:checked disabled/>
            <span slot="label" class:text-disabled={disabled}>Dono el meu consentiment a la UPC per al tractament de les dades de caràcter personal.
                Les dades recollides es tractaran tal com es descriu a la <a
                        href="https://www.upc.edu/ca/avis-legal/informacio-i-acces-a-les-dades-personals"
                        target="_blank" class="text-link">taula d'informació i accés a les dades personals</a>.
            </span>
        </FormField>
        <div class="submit-button-div {complete ? '': 'disabled'}">
            <Button type="submit" variant="unelevated" style="margin-top: 10px" on:click={createRose}
                    class="{complete ? '' : 'submit-button-disabled'}" disabled="{disabled}">
                <Label>Crear Rosa</Label>
            </Button>
        </div>
    </div>
{/if}
<Dialog
        bind:open={openDialog}
        aria-labelledby="simple-title"
        aria-describedby="simple-content"
>
    <Title id="simple-title">
        Hi ha hagut un error
    </Title>
    <Content id="simple-content">
        {message}
    </Content>
    <Actions>
        <Button>
            <Label>D'acord</Label>
        </Button>
    </Actions>
</Dialog>

<style>
    .text-disabled {
        opacity: 50%;
    }

    #form-div {
        margin: 1vh 0;
        display: flex;
        gap: 15px;
        flex-direction: column;
        align-items: stretch;
    }

    #creation-disclaimer-text {
        margin-top: 0;
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.875rem;
    }

    :global(#creation-disclaimer-text > i) {
        transform: rotate(180deg);
    }

    :global(.submit-button-div) {
        align-self: flex-start;
    }

    :global(.submit-button-div.disabled) {
        cursor: not-allowed;
    }

    :global(.submit-button-disabled) {
        pointer-events: none;
    }

</style>